import React, { useContext, useMemo } from 'react'
import { Checkbox, CheckboxIndicator } from '@radix-ui/react-checkbox'

import UUIThemeContext from '../theme/context'

import { UUICheckboxProps } from '../types/UUICheckbox'

export function UUICheckboxCheckIndicator({ uiSize = 'normal' }: { uiSize: UUICheckboxProps['uiSize'] }) {
  return 'small' === uiSize ? (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path
        d="M10.5 4.0382L4.71429 9.9059L1.5 6.6461L2.78571 5.3422L4.71429 7.2981L9.21429 2.7343L10.5 4.0382Z"
        className="UUICheckboxCheckIndicator-path"
      />
    </svg>
  ) : (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path
        d="M12.25 4.71126L5.5 11.55691L1.75 7.75377L3.25 6.23251L5.5 8.5144L10.75 3.19L12.25 4.71126Z"
        className="UUICheckboxCheckIndicator-path"
      />
    </svg>
  )
}
const UUICheckboxCheckIndicatorMemo = React.memo(
  UUICheckboxCheckIndicator,
  (prevProps, nextProps) => prevProps.uiSize === nextProps.uiSize
)

export default function UUICheckbox({
  checked,
  defaultChecked,
  onChange,
  disabled,
  required,
  name,
  value,
  uiSize = 'normal',
  uiStyle = 'filled',
  uiInvalid = false,
  ['aria-label']: ariaLabel,
  indicator,
  children
}: UUICheckboxProps) {
  const theme = useContext(UUIThemeContext)

  const { checkboxClassName, checkboxIndicatorClassName, checkboxLabelClassName } = useMemo(() => {
    const style = uiStyle
    const size = uiSize
    const variant = disabled ? 'disabled' : uiInvalid ? 'invalid' : 'normal'
    const themeChecked = true === checked ? 'true' : false === checked ? 'false' : 'null'

    const checkboxClassName =
      'function' === typeof theme.Checkbox?.element
        ? theme.Checkbox.element({ style, size, variant, checked })
        : 'object' === typeof theme.Checkbox?.element
          ? theme.Checkbox.element[style]?.[size]?.[variant]?.[themeChecked] || ''
          : ''
    const checkboxIndicatorClassName =
      'function' === typeof theme.Checkbox?.indicator
        ? theme.Checkbox.indicator({ style, size, variant, checked })
        : 'object' === typeof theme.Checkbox?.indicator
          ? theme.Checkbox.indicator[style]?.[size]?.[variant]?.[themeChecked] || ''
          : ''
    const checkboxLabelClassName =
      'function' === typeof theme.Checkbox?.label
        ? theme.Checkbox.label({ style, size, variant, checked })
        : 'object' === typeof theme.Checkbox?.label
          ? theme.Checkbox.label[style]?.[size]?.[variant]?.[themeChecked] || ''
          : ''
    return { checkboxClassName, checkboxIndicatorClassName, checkboxLabelClassName }
  }, [uiStyle, uiSize, disabled, uiInvalid, checked, theme.Checkbox])

  const checkbox = (
    <Checkbox
      className={checkboxClassName}
      checked={null === checked ? 'indeterminate' : checked}
      defaultChecked={null === defaultChecked ? 'indeterminate' : defaultChecked}
      onCheckedChange={checkedValue => onChange?.('indeterminate' === checkedValue ? null : Boolean(checkedValue))}
      disabled={disabled}
      required={required}
      name={name}
      value={value}
      aria-label={ariaLabel}
    >
      <CheckboxIndicator className={checkboxIndicatorClassName}>
        {true === checked ? (
          indicator ? (
            indicator({
              checked,
              defaultChecked,
              onChange,
              disabled,
              required,
              name,
              value,
              uiSize,
              uiStyle
            })
          ) : (
            <UUICheckboxCheckIndicatorMemo uiSize={uiSize} />
          )
        ) : null}
      </CheckboxIndicator>
    </Checkbox>
  )
  return children ? (
    <label className="tw:flex">
      {checkbox}
      <span className={checkboxLabelClassName}>{children}</span>
    </label>
  ) : (
    checkbox
  )
}
